import {useCallback, useEffect, useState} from 'react';

import {
  CourierType,
  CursorInput,
  WalletActivationStatus,
  useCouriersWithCursorPaginationQuery,
} from '../../../generated';
import {readableWalletStatus} from '../../../utils/reusablefunctions';
import {TableColumnType, TableDataType} from '../../../utils/types/table.types';

function useGetCouriers(
  courierType?: CourierType,
  walletStatus?: WalletActivationStatus[],
  searchTerm?: string
) {
  const [count, setCount] = useState<number>(1);
  const [loading, setLoading] = useState(false);

  const {
    data: allCouriers,
    loading: dataLoading,
    error,
    refetch,
  } = useCouriersWithCursorPaginationQuery({
    variables: {
      input: {
        cursor: {
          first: 20,
        },
        courierType,
        search: searchTerm || '',
        walletStatus,
      },
    },
  });

  const loadMore = useCallback(
    async (next: boolean) => {
      setLoading(true);
      const cursorLoad: CursorInput = {};
      if (next) {
        cursorLoad.after =
          allCouriers?.couriersWithCursorPagination.pageInfo.endCursor;
        cursorLoad.first = 20;
      } else {
        cursorLoad.before =
          allCouriers?.couriersWithCursorPagination.pageInfo.startCursor;
        cursorLoad.last = 20;
      }
      const res = await refetch({
        input: {
          cursor: cursorLoad,
          courierType,
          walletStatus,
          search: searchTerm,
        },
      });
      if (res.data) {
        next ? setCount(count + 1) : setCount(count - 1);
      }
      setLoading(false);
    },
    [
      allCouriers?.couriersWithCursorPagination.pageInfo.endCursor,
      allCouriers?.couriersWithCursorPagination.pageInfo.startCursor,
      count,
      courierType,
      refetch,
      searchTerm,
      walletStatus,
    ]
  );

  const [data, setData] = useState<TableDataType[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [columns, setColumns] = useState<TableColumnType[]>([
    'name',
    'phone_number',
    'vehicle_type',
    'license_plate',
    'status',
  ]);
  const parentColumns: TableColumnType[] = [
    'name',
    'phone_number',
    'vehicle_type',
    'license_plate',
    'cashtag',
    'status',
  ];
  const sortingColumns: TableColumnType[] = ['name', 'status'];

  function capitalize(word: string) {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }
  const formatEnumContent = useCallback((str: string) => {
    if (str.includes('_')) return capitalize(str.split('_').join(' '));
    return capitalize(str);
  }, []);

  useEffect(() => {
    setLoading(dataLoading);
  }, [dataLoading]);

  useEffect(() => {
    if (!allCouriers?.couriersWithCursorPagination?.edges) return;
    setData(
      allCouriers?.couriersWithCursorPagination?.edges.map((courier) => ({
        id: courier.node.id || '',
        name: [
          courier.node.user?.name || '-',
          courier.node.avatar?.small || '',
        ],
        phone_number: `+${courier.node.user?.phone?.countryCode || '-'}${
          courier.node.user?.phone?.number || '-'
        }`,
        vehicle_type: formatEnumContent(courier.node.vehicleType || '-'),
        license_plate: courier.node.licensePlate || '-',
        cashtag: courier.node.tag || '-',
        status:
          (courier.node.wallet?.activation?.status &&
            readableWalletStatus(courier.node.wallet.activation.status)) ||
          '',
      })) || []
    );
  }, [allCouriers, formatEnumContent]);

  useEffect(() => {
    setCount(1);
  }, [courierType, walletStatus]);

  return {
    data,
    setData,
    allCouriers,
    count,
    loadMore,
    loading,
    error,
    columns,
    setColumns,
    parentColumns,
    selectedIds,
    setSelectedIds,
    sortingColumns,
    formatEnumContent,
    searchTerm,
  };
}

export default useGetCouriers;
